import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useGameStore } from '@/stores/gameStore';
import { useCurrentSession } from '@/stores/gameSessionStore';
import { useSessionRecovery } from '@/utils/sessionRecovery';
import UnifiedQuestionDisplay from '@/components/UnifiedQuestionDisplay';
import MobileGameOverlay from '@/components/MobileGameOverlay';
import { DEFAULT_TIMER_CONFIG } from '@/config/timerConfig';
import useSessionPhaseTimer from '@/hooks/useSessionPhaseTimer';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { scaleFontSize } from '@/utils/scaleFontSize';

const QUESTION_PAGE_FONT_SCALE = 1.2;

const QuestionScreen = () => {
  const navigate = useSafeNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const currentSession = useCurrentSession();
  const activeSession = sessionId ? currentSession : null;
  const { getStoredSessionData } = useSessionRecovery();
  const {
    getCurrentQuestion,
    currentQuestionIndex,
    getTotalQuestions,
    gameState,
    setGameState,
    questions,
    initializeGame,
    syncQuestionIndex,
    hydrateSessionPlayers,
    setCurrentPlayerId
  } = useGameStore();
  const gameMode = useGameStore(state => state.gameMode);
  const isAiMode = gameMode === 'ai';
  const aiQuestionAdvanceRef = useRef(false);
  const shouldUseLocalQuestionState = isAiMode || !sessionId;

  const currentQuestion = getCurrentQuestion();
  const totalQuestions = getTotalQuestions();
  const playerIdFromParams = searchParams.get('playerId');

  const fallbackQuestionSeconds = (() => {
    const configuredDuration = activeSession?.currentPhaseDurationSeconds;
    if (typeof configuredDuration === 'number' && configuredDuration > 0) {
      return configuredDuration;
    }

    const questionDuration = currentQuestion?.questionReadingTimeSeconds;
    if (typeof questionDuration === 'number' && questionDuration > 0) {
      return questionDuration;
    }

    return DEFAULT_TIMER_CONFIG.questionPhaseSeconds;
  })();

  const questionTimeRemaining = useSessionPhaseTimer(activeSession, fallbackQuestionSeconds);

  // Debug logging for QuestionScreen state

  // Check if game is properly initialized, with intelligent recovery
  useEffect(() => {
    if (questions.length === 0) {
      console.warn('⚠️ QuestionScreen: No questions found');

      // If we have a current session, try to initialize from session data
      if (activeSession && activeSession.questions.length > 0) {
        initializeGame(activeSession.id, activeSession.questions);
        return;
      }

      // If we have stored session data, try recovery
      const storedData = getStoredSessionData();
      if (storedData.canRecover && sessionId) {
        // Don't redirect immediately, let the session recovery system handle it
        return;
      }

      // Only redirect if we're absolutely sure there's no way to recover
      if (!sessionId && !activeSession) {
        console.warn('⚠️ QuestionScreen: No session data available, redirecting to lobby');
        navigate('/lobby');
      }
      // Session data exists but not yet initialized in store
    }
  }, [questions.length, activeSession, sessionId, initializeGame, getStoredSessionData, navigate]);

  // Keep player roster in sync with session data
  useEffect(() => {
    if (activeSession?.players) {
      hydrateSessionPlayers(activeSession.players, playerIdFromParams || null);
      setCurrentPlayerId(playerIdFromParams || null);
    }
  }, [activeSession?.players, hydrateSessionPlayers, setCurrentPlayerId, playerIdFromParams]);

  // Sync question index from session (in addition to auto-navigation hook)
  useEffect(() => {
    if (questions.length > 0 &&
        activeSession?.currentQuestionIndex !== undefined &&
        activeSession.currentQuestionIndex !== currentQuestionIndex) {
      syncQuestionIndex(activeSession.currentQuestionIndex);
    }
  }, [activeSession?.currentQuestionIndex, currentQuestionIndex, questions.length, syncQuestionIndex]);

  // Auto-navigate when game state changes to running - DISABLED: Now handled by StudentAutoNavigation
  // useEffect(() => {
  //   if (gameState === 'running') {
  //     navigate('/game');
  //   }
  // }, [gameState, navigate]);

  // Start question phase when component mounts (only once)
  useEffect(() => {
    if (shouldUseLocalQuestionState && gameState !== 'question' && currentQuestion) {
      setGameState('question');
    }
    // Game state is already 'question'
  }, [currentQuestion, gameState, setGameState, shouldUseLocalQuestionState]); // Only depend on currentQuestion to avoid loops

  useEffect(() => {
    if (!isAiMode) {
      aiQuestionAdvanceRef.current = false;
      return;
    }

    if (questionTimeRemaining > 0) {
      aiQuestionAdvanceRef.current = false;
      return;
    }

    if (!currentQuestion || aiQuestionAdvanceRef.current) {
      return;
    }

    aiQuestionAdvanceRef.current = true;
    setGameState('running');
    navigate('/game');
  }, [isAiMode, questionTimeRemaining, currentQuestion, setGameState, navigate]);

  if (!currentQuestion) {
    return (
      <div style={{
        background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '3rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: scaleFontSize('3rem', QUESTION_PAGE_FONT_SCALE), marginBottom: '1rem' }}>🔄</div>
          <h2 style={{ fontSize: scaleFontSize('1.8rem', QUESTION_PAGE_FONT_SCALE), margin: 0 }}>Loading question...</h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileGameOverlay />
      <UnifiedQuestionDisplay
        question={currentQuestion}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={totalQuestions}
        timeRemaining={questionTimeRemaining}
        isTeacher={false}
        showCorrectAnswer={false}
        sessionId={sessionId || undefined}
        playerId={searchParams.get('playerId') || undefined}
        fontScale={QUESTION_PAGE_FONT_SCALE}
      />
    </>
  );
};

export default QuestionScreen;
